import React, { createContext, useContext, useEffect, useMemo, useState } from 'react'
import { css } from '@emotion/core'
import ZionForm, { FormSelect, FormToggle, FormTextField, useZionForm } from '@fs/zion-form'
import { geoData } from '@fs/zion-config'
import { useQueryParams } from '@fs/zion-router'
import { NoticeImportant, Person } from '@fs/zion-icon'
import { Button, colors, InfoSheet, useInfoSheet, Row, Divider, Separator } from '@fs/zion-ui'
import * as Yup from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'
import { i18n } from '@fs/zion-locale'
import { useShowTemple, useCanAddPersonalData } from '@fs/zion-permissions'
import file from './countryNamesWithCodes.json'
import useUserSegment from '../../shared/hooks/useUserSegment'

const countryOptions = Object.values(file)
const countryCodes = Object.keys(file)
const countryOverrideOptions = createCountryOverrideOptions()

/**
 * @typedef {Object} VisibilityOptions
 * @property {string} ancestry
 * @property {string} countryCode
 * @property {boolean} member
 * @property {boolean} newUser
 * @property {boolean} temple
 * @property {'EMPTY' | 'SPARSE' | 'FULL'} treeLevel
 * @property {'CONTRIBUTOR' | 'NON-CONTRIBUTOR' | 'HELPER'} userLevel
 */

/**
 * @type {VisibilityOptions}
 */
const DEFAULT_USER_VISIBILITY_OVERRIDE = {
  ancestryCode: '',
  ancestryNumber: '',
  ancestry: {},
  canAddPersonalData: false,
  countryCode: '',
  member: false,
  newUser: false,
  temple: false,
  treeLevel: '',
  userLevel: '',
  override: false,
}

const VisibilityOverrideContext = createContext({
  /** @type {VisibilityOptions} */
  visibilityOverride: {},
  /**
   * @param {VisibilityOptions} visibilityOverrides
   */
  // eslint-disable-next-line no-unused-vars
  setVisibilityOverride: (visibilityOverrides) => {},
})

let initialFormValues
function stringToBoolean(value) {
  return value === 'true' || value === 'false' ? value === 'true' : value
}
function stringsToBoolean(object) {
  return Object.keys(object).reduce((obj, key) => ({ ...obj, [key]: stringToBoolean(object[key]) }), {})
}
/**
 * Provides the current user visibility override and stores it in query params.
 * @param {{ children: React.ReactNode }} props
 */
export function VisibilityOverrideProvider({ children }) {
  const { query, setQuery } = useQueryParams()

  const overrideObject = useMemo(() => {
    const visibilityOverride = stringsToBoolean(query)
    if (!initialFormValues) {
      initialFormValues = visibilityOverride
    }
    return {
      visibilityOverride: visibilityOverride.override ? visibilityOverride : {},
      setVisibilityOverride: setQuery,
      clearVisibilityOverride: () => {
        setQuery({ override: true }, true)
      },
    }
  }, [query, setQuery])

  const enableVisibilityOverrides = typeof query.override === 'string'
  if (enableVisibilityOverrides) {
    return (
      <VisibilityOverrideContext.Provider value={overrideObject}>
        {children}
        <VisibilityOverride />
      </VisibilityOverrideContext.Provider>
    )
  }
  return children
}

/**
 * Hook to get the current user visibility override.
 */
export function useVisibilityOverride() {
  return useContext(VisibilityOverrideContext)
}

export const visibilityOverrideSchema = Yup.object().shape({
  ancestry: Yup.string(),
  treeLevel: Yup.string().oneOf(['EMPTY', 'SPARSE', 'FULL'], 'Please select a tree level'),
  member: Yup.boolean().when('temple', {
    is: true,
    then: () => Yup.boolean().oneOf([true], 'Member must be enabled if temple is enabled'),
  }),
  newUser: Yup.boolean(),
  userLevel: Yup.string().oneOf(['CONTRIBUTOR', 'NON-CONTRIBUTOR', 'HELPER'], 'Please select a user level'),
  temple: Yup.boolean(),
  canAddPersonalData: Yup.boolean(),
  countryCode: Yup.string().oneOf(countryCodes.concat(''), 'Please select a country'),
})

const highlightCss = css`
  color: ${colors.blue50};
`

// Helper function to set form value if field is not present in initial form values
function setFieldIfNotInUrl(methods, fieldName, value) {
  if (!Object.hasOwn(initialFormValues, fieldName) && value !== undefined) {
    methods.setValue(fieldName, value)
  }
}

function populateAncestryIfNotInUrl(methods, userData) {
  if (Object.hasOwn(initialFormValues, 'ancestryCode') || !userData.ancestry) {
    return
  }
  const ancestryEntries = Object.entries(userData.ancestry)
  if (ancestryEntries.length > 0) {
    // Find the country with the highest ancestry number
    const [maxCountryCode, maxCount] = ancestryEntries.reduce(
      (max, current) => (current[1] > max[1] ? current : max),
      ancestryEntries[0]
    )
    methods.setValue('ancestryCode', maxCountryCode)
    methods.setValue('ancestryNumber', maxCount)
  }
}

function setUserLevel(methods, userLevel) {
  if (!userLevel) {
    return
  }
  const normalizedUserLevel = userLevel.replaceAll('_', '-')
  methods.setValue('userLevel', normalizedUserLevel)
}

// Initialize form with URL overrides and fill missing fields with user data
function initializeFormWithUrlOverrides(
  methods,
  userData,
  canSeeTemple,
  canAddPersonalData,
  countryData = geoData.country
) {
  Object.keys(initialFormValues).forEach((key) => {
    methods.setValue(key, initialFormValues[key])
  })
  setFieldIfNotInUrl(methods, 'countryCode', countryData)
  setFieldIfNotInUrl(methods, 'member', userData.member)
  setFieldIfNotInUrl(methods, 'newUser', userData.newUser)
  setFieldIfNotInUrl(methods, 'treeLevel', userData.treeLevel)
  if (!Object.hasOwn(initialFormValues, 'userLevel')) {
    setUserLevel(methods, userData.userLevel)
  }
  populateAncestryIfNotInUrl(methods, userData)
  setFieldIfNotInUrl(methods, 'temple', canSeeTemple)
  setFieldIfNotInUrl(methods, 'canAddPersonalData', canAddPersonalData)
}

/**
 * Puts a button in the bottom-left corner of the screen that opens a dialog to override the current user's visibility.
 * This is useful for testing the visibility of widgets for different user personas.
 * Must be wrapped in a VisibilityOverrideProvider to work.
 */
export default function VisibilityOverride() {
  const infoSheet = useInfoSheet()
  const { visibilityOverride, setVisibilityOverride, clearVisibilityOverride } = useVisibilityOverride()
  const { query, setQuery } = useQueryParams()

  // Get actual user values for pre-populating the form
  const { data: userData, isLoading: isUserDataLoading } = useUserSegment()
  const [canSeeTemple] = useShowTemple()
  const [canAddPersonalData, permissionStatus] = useCanAddPersonalData()
  const isAnyDataLoading = isUserDataLoading || permissionStatus === 'loading'

  function highlightOverriddenField(string) {
    return typeof visibilityOverride[string] !== 'undefined' ? highlightCss : null
  }

  // strip out temple=true from the URL if canSeeTemple is false
  useEffect(() => {
    if (canSeeTemple === false && typeof query.temple === 'string') {
      const { temple, ...rest } = query
      setQuery(rest, true)
    }
  }, [canSeeTemple, query, setQuery])

  const onChange = ({ target }) => {
    const { name, value, checked } = target

    let override
    if (name === 'ancestryNumber' || name === 'ancestryCode') {
      // ancestryNumber always gives checked false, so handling it differently.
      // ancestryNumber is the number of ancestors with a given ancestry event (birth, death) in a specific country within 5 gens
      override = {
        [name]: value,
        ancestry: {
          [name === 'ancestryCode' ? name : visibilityOverride.ancestryCode]:
            name === 'ancestryNumber' ? value : visibilityOverride.ancestryNumber,
        },
      }
    } else {
      override = {
        [name]: checked ?? value,
      }
    }
    setVisibilityOverride(override)
  }

  const methods = useZionForm({
    mode: 'all',
    defaultValues: DEFAULT_USER_VISIBILITY_OVERRIDE,
    resolver: yupResolver(visibilityOverrideSchema),
  })
  const { errors } = methods.formState
  const setQueryParams = () => {
    const values = methods.getValues()
    setVisibilityOverride(values)
  }
  const handleReset = () => {
    clearVisibilityOverride()
    methods.reset()
    initializeFormWithUrlOverrides(methods, userData, canSeeTemple, canAddPersonalData)
    methods.setValue('override', true)
  }

  const [initialized, setInitialized] = useState(false)

  useEffect(() => {
    if (initialized) {
      return
    }

    // Wait for data to load before initializing
    if (isAnyDataLoading || !userData) {
      return
    }

    initializeFormWithUrlOverrides(methods, userData, canSeeTemple, canAddPersonalData)

    setInitialized(true)
  }, [initialized, isAnyDataLoading, userData, canSeeTemple, canAddPersonalData, methods])

  return (
    <>
      <VisibilityOverrideButton onClick={infoSheet.handleClick} enabled={visibilityOverride.override} />
      <InfoSheet {...infoSheet} title="User Visibility Override">
        <ZionForm onChange={onChange} methods={methods}>
          <FormSelect
            name="ancestryCode"
            label={<span css={highlightOverriddenField('ancestryCode')}>Country of Ancestry</span>}
            options={countryOverrideOptions}
          />
          <FormTextField
            name="ancestryNumber"
            label={<span css={highlightOverriddenField('ancestryNumber')}>Ancestry Number</span>}
            placeholder="Example: 1"
          />
          <Separator size="xs" />
          <Divider />
          <Separator size="xs" />
          <FormSelect
            name="treeLevel"
            label={<span css={highlightOverriddenField('treeLevel')}>Tree Level</span>}
            options={[
              { value: 'EMPTY', label: 'Empty' },
              { value: 'SPARSE', label: 'Sparse' },
              { value: 'FULL', label: 'Full' },
            ]}
          />
          <FormSelect
            name="userLevel"
            label={<span css={highlightOverriddenField('userLevel')}>User Level</span>}
            options={[
              { value: 'CONTRIBUTOR', label: 'Contributor' },
              { value: 'NON-CONTRIBUTOR', label: 'Non-Contributor' },
              { value: 'HELPER', label: 'Helper' },
            ]}
          />
          <FormToggle
            name="canAddPersonalData"
            label={<span css={highlightOverriddenField('canAddPersonalData')}>Can Add Personal Data</span>}
          />
          <FormSelect
            name="countryCode"
            label={<span css={highlightOverriddenField('countryCode')}>Country Code</span>}
            options={countryOverrideOptions}
          />
          <FormToggle name="newUser" label={<span css={highlightOverriddenField('newUser')}>New User</span>} />
          <FormToggle
            name="member"
            label={<span css={highlightOverriddenField('member')}>Member</span>}
            color={errors.member ? colors.red70 : undefined}
          />
          {errors.member && (
            <>
              <NoticeImportant color={colors.red70} />
              <span css={{ color: colors.red70, fontSize: 12, marginLeft: 8 }}>{errors.member.message}</span>
            </>
          )}
          {canSeeTemple && (
            <FormToggle name="temple" label={<span css={highlightOverriddenField('temple')}>Temple</span>} />
          )}
          <FormToggle
            name="override"
            label={<span css={highlightOverriddenField('override')}>Override Enabled</span>}
          />
          <Separator size="md" />
          <Row alignX="end">
            <Button type="submit" onClick={setQueryParams}>
              Set Query Params
            </Button>
            <Button type="button" onClick={handleReset} emphasis="medium">
              Reset
            </Button>
          </Row>
        </ZionForm>
      </InfoSheet>
    </>
  )
}

const buttonStyles = css`
  position: fixed;
  bottom: 120px;
  height: 40px;
  width: 32px;
  background: ${colors.purple10};
  color: ${colors.gray100};
  border: none;
  border-start-start-radius: 0;
  border-start-end-radius: 5px;
  border-end-start-radius: 5px;
  border-end-end-radius: 0;
  box-shadow: 0 3px 10px rgb(0 0 0 / 0.2);
  z-index: 2;
  cursor: pointer;

  &:hover {
    box-shadow: 0 3px 10px rgb(0 0 0 / 0.3);
  }

  & > span:first-of-type {
    position: absolute;
    top: 8px;
    inset-inline-start: 3px;
  }
`

/**
 * Button that opens the VisibilityOverride dialog.
 * @param {{ onClick: () => void, enabled: boolean }} props
 */
function VisibilityOverrideButton({ onClick, enabled }) {
  return (
    <button
      css={buttonStyles}
      onClick={onClick}
      type="button"
      data-testid="user-visibility-override-button"
      aria-label={i18n.t('home-portal.home.visibility-override', 'Visibility Override')}
    >
      <Person />
      {/* Indicator dot when overrides are enabled */}
      <span
        css={{
          position: 'absolute',
          bottom: 8,
          insetInlineEnd: 4,
          height: 8,
          width: 8,
          borderRadius: '50%',
          background: enabled ? 'red' : 'transparent',
        }}
      />
    </button>
  )
}

function createCountryOverrideOptions() {
  return countryOptions.map((country, i) => ({ value: countryCodes[i], label: country }))
}
